<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import type { INavLinkItem } from '~/components/navbar/NavigationLinks.vue'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import { sendMenuClick } from '~/composables/useGtmEvents'

const props = defineProps<{
  navLinks: INavLinkItem[]
  megamenuData: MegamenuNavItem[]
}>()

const emit = defineEmits<{
  (e: 'navbarClose'): void
  (e: 'subviewOpen', value: boolean): void
}>()

type View = 'list' | 'sections' | 'panel'
const view = ref<View>('list')

const itemByPath = computed(() => {
  const map = new Map<string, MegamenuNavItem>()
  for (const item of props.megamenuData) {
    map.set(item.path, item)
  }
  return map
})

function getMegamenuItem(path: string): MegamenuNavItem | undefined {
  return itemByPath.value.get(path)
}

function hasSubmenu(item: INavLinkItem) {
  const full = getMegamenuItem(item.path)
  return (
    full?.primaryLink != null ||
    (full?.sections?.length ?? 0) > 0 ||
    (full?.items?.length ?? 0) > 0
  )
}

function hasSections(item: MegamenuNavItem) {
  return (item.sections?.length ?? 0) > 0
}

const expandedIndex = ref<number | null>(null)
const selectedSectionIndex = ref<number | null>(null)

const expandedItem = computed(() => {
  if (expandedIndex.value == null) return null
  const link = props.navLinks[expandedIndex.value]
  if (!link) return null
  return getMegamenuItem(link.path) ?? null
})

const expandedTitle = computed(() => {
  if (expandedIndex.value == null) return ''
  const link = props.navLinks[expandedIndex.value]
  return link?.text ?? ''
})

const selectedSection = computed((): MegamenuSection | null => {
  const item = expandedItem.value
  if (item?.sections == null || selectedSectionIndex.value == null) return null
  const s = item.sections[selectedSectionIndex.value]
  return s ?? null
})

function openItem(index: number) {
  expandedIndex.value = index
  selectedSectionIndex.value = null
  const item = getMegamenuItem(props.navLinks[index]?.path ?? '')
  if (item && hasSections(item)) {
    view.value = 'sections'
  } else {
    view.value = 'panel'
  }
  emit('subviewOpen', true)
}

function openSection(index: number) {
  selectedSectionIndex.value = index
  view.value = 'panel'
  emit('subviewOpen', true)
}

function goBackToSections() {
  view.value = 'sections'
  selectedSectionIndex.value = null
  if (view.value === ('list' as View)) {
    emit('subviewOpen', false)
  }
}

function goBackToList() {
  view.value = 'list'
  expandedIndex.value = null
  selectedSectionIndex.value = null
  emit('subviewOpen', false)
}

function handleSimpleItemClick(item: INavLinkItem) {
  sendMenuClick({
    menuName: item.text,
    itemLabel: item.text,
    itemPath: item.path,
  })
  emit('navbarClose')
}
</script>

<template>
  <div data-test-id="megamenu-mobile" class="flex flex-col h-full min-h-0">
    <!-- Main list -->
    <template v-if="view === 'list'">
      <ul data-test-id="megamenu-mobile-nav-list" class="flex flex-col">
        <li
          v-for="(item, index) in navLinks"
          :key="item.path"
          :data-test-id="`megamenu-mobile-nav-item-${index}`"
          class="border-b border-gray-200 px-4"
        >
          <NuxtLinkLocale
            v-if="!hasSubmenu(item)"
            :data-test-id="`megamenu-mobile-nav-link-${index}`"
            class="flex items-center justify-between py-4 font-medium text-stone-800 no-underline"
            :to="item.path"
            @click="handleSimpleItemClick(item)"
          >
            {{ item.text }}
          </NuxtLinkLocale>
          <button
            v-else
            :data-test-id="`megamenu-mobile-nav-button-${index}`"
            type="button"
            class="flex w-full items-center justify-between py-3 font-medium text-left text-stone-800"
            @click="openItem(index)"
          >
            {{ item.text }}
            <IconChevronRight class="h-5 w-5 shrink-0 text-gray-400" />
          </button>
        </li>
      </ul>
    </template>
    <!-- Sections: buttons to open each section -->
    <MegamenuMobileSections
      v-else-if="view === 'sections' && expandedItem"
      :item="expandedItem"
      :title="expandedTitle"
      :expand-in-panel="expandedItem.expandInPanel ?? false"
      class="h-full min-h-0"
      @back="goBackToList"
      @open-section="openSection"
      @navbar-close="emit('navbarClose')"
    />
    <!-- Panel: single section content or item items (kits) -->
    <MegamenuMobilePanel
      v-else-if="view === 'panel' && expandedItem"
      :item="expandedItem"
      :title="selectedSection ? selectedSection.title : expandedTitle"
      :section="selectedSection ?? undefined"
      class="h-full min-h-0"
      @back="selectedSection != null ? goBackToSections() : goBackToList()"
      @navbar-close="emit('navbarClose')"
    />
  </div>
</template>
